import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useParticipationTableContext } from 'pages/Dashboard/Sessions/Details/components/ParticipationsTable/ParticipationTableContext';
import { TableParticipationHeader } from 'pages/Dashboard/Sessions/Details/components/TableParticipationHeader';
import { TextLoadingPlaceholder, Tooltip } from '@app/core';
import {
  Filter,
  formatFieldChoice,
} from 'pages/Dashboard/Sessions/Details/components/Filter';
import {
  CriterionStatusEnum,
  CustomSelectChoice,
  FieldChoice,
  IndexedParticipation,
  ParticipantFieldChoice,
} from '@app/api';
import classNames from 'classnames';
import { TableColumnHeader } from './TableColumnHeader';
import { FieldConfig } from '../../hooks/useFieldConfig';
import { useTranslation } from 'react-i18next';
import { useAllOtherTranslations } from 'lib/formUtils';
import { CriterionStatusText } from 'components/CriterionStatusText';

const TOOLTIP_DELAY = 800;

const useStyles = createUseStyles((theme: Theme) => ({
  ellipsis: {
    display: '-webkit-box',
    overflow: 'hidden',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': 2,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    width: 'max-content',
    maxWidth: theme.marginBase * 28,
  },
  maxColumnSize: {
    maxWidth: theme.marginBase * 35,
  },
  minColumnSize: {
    minWidth: theme.marginBase * 10,
  },
}));

interface ScreenerHeaderProps {
  config: FieldConfig;
  values?: Array<FieldChoice | ParticipantFieldChoice>;
  loadOnlyUsedValues?: boolean;
  loadOldValues?: boolean;
}

export const ScreenerHeader = ({
  config,
  values,
  loadOnlyUsedValues,
  loadOldValues,
}: ScreenerHeaderProps) => {
  const { session, filtering, sorting } = useParticipationTableContext();
  const classes = useStyles();
  const currentFilter = filtering.getFieldCurrentFilter(config.field);
  return (
    <th key={config.field}>
      <TableColumnHeader>
        <TableParticipationHeader
          fieldKey={config.field}
          fieldType={config.type!}
          currentSort={sorting.sort}
          onSortClick={sorting.handleSortClick}
        >
          <Tooltip
            arrow
            placement="right"
            title={config.label}
            enterDelay={TOOLTIP_DELAY}
            enterNextDelay={TOOLTIP_DELAY}
          >
            <span className={classes.ellipsis}>{config.label}</span>
          </Tooltip>
        </TableParticipationHeader>
        <Filter
          fieldRef={config.field}
          fieldLabel={config.label}
          fieldType={config.type!}
          fieldValues={values?.map(formatFieldChoice)}
          sessionId={session.id}
          currentFilterData={currentFilter}
          onFilterDataChange={filtering.handleFilterChange}
          loadOnlyUsedValues={loadOnlyUsedValues}
          loadOldValues={loadOldValues}
        />
      </TableColumnHeader>
    </th>
  );
};

export const ScreenerCell = ({
  participation,
  config,
}: {
  participation: IndexedParticipation;
  config: FieldConfig;
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const otherTranslations = useAllOtherTranslations();

  const getFieldDisplayValue = () => {
    const formResponse = participation.formResponses?.find(
      (fr) => fr.fieldRef === config.field,
    );
    if (!formResponse) return '';

    return formResponse.labels
      .map((label, index) => {
        const ref = formResponse.values[index];

        if (
          ref === CustomSelectChoice.Other &&
          !otherTranslations.has(label.trim().toLowerCase())
        ) {
          return `${t('screeningForm.other')} : ${label}`;
        }

        return label;
      })
      .join(', ');
  };

  const status: CriterionStatusEnum =
    participation.criteriaStatus?.[config.field] || CriterionStatusEnum.IGNORE;

  return (
    <td key={config.field}>
      <Tooltip
        arrow
        placement="right"
        title={getFieldDisplayValue()}
        enterDelay={TOOLTIP_DELAY}
        enterNextDelay={TOOLTIP_DELAY}
      >
        <CriterionStatusText
          criterionStatus={status}
          className={classNames(
            classes.ellipsis,
            classes.minColumnSize,
            classes.maxColumnSize,
          )}
        >
          {getFieldDisplayValue()}
        </CriterionStatusText>
      </Tooltip>
    </td>
  );
};

export const ScreenerLoader = ({ config }: { config: FieldConfig }) => {
  return (
    <td key={config.field}>
      <TextLoadingPlaceholder sample={config.label.slice(0, 36)} />
    </td>
  );
};
